import { format, isValid, parse, parseISO } from 'date-fns';

import { DATE_FORMATS, type DateInput } from './formats';
import { wallTimeInZone } from './zonedWallTime';

/**
 * Any input as a Date, or null when it is missing or not a date.
 *
 * Strings are read as ISO 8601 first, so a bare `YYYY-MM-DD` is local midnight (not UTC
 * midnight as `new Date('YYYY-MM-DD')` would make it). Anything else falls back to the
 * engine's own parser, as moment did.
 *
 * @example
 * toDate('2024-01-15'); // local midnight, 15 January 2024
 * toDate(1705334400000); // from a timestamp in milliseconds
 * toDate(null); // null
 */
export function toDate(input: DateInput): Date | null {
    if (!input) return null;

    if (input instanceof Date) {
        return Number.isNaN(input.getTime()) ? null : input;
    }

    if (typeof input === 'number') {
        const fromNumber = new Date(input);
        return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
    }

    const iso = parseISO(input);
    if (isValid(iso)) return iso;

    const loose = new Date(input);
    return Number.isNaN(loose.getTime()) ? null : loose;
}

/** An ISO 8601 string as a Date, or null when it is missing or invalid. */
export function parseISOString(isoString: string | null | undefined): Date | null {
    if (!isoString) return null;
    return toDate(isoString);
}

/**
 * A string in exactly `formatString` (date-fns tokens) as a Date, or null.
 *
 * Strict: the string must be what formatting the parsed day gives back, so a missing
 * leading zero, a two-digit year or a day the month lacks ("31-09-2026") is rejected
 * rather than read loosely or rolled over.
 *
 * @example
 * parseDate('2024-01-15'); // local midnight, 15 January 2024
 * parseDate('15/01/2024', 'dd/MM/yyyy'); // the same day
 * parseDate('1-9-2024', 'dd-MM-yyyy'); // null
 */
export function parseDate(
    dateStr: string,
    formatString: string = DATE_FORMATS.internal
): Date | null {
    if (!dateStr) return null;
    const parsed = parse(dateStr, formatString, new Date());
    if (!isValid(parsed)) return null;
    return format(parsed, formatString) === dateStr ? parsed : null;
}

/** Whether the input reads as a date. */
export function isValidDate(date: DateInput): boolean {
    return toDate(date) !== null;
}

export interface ParseTsOptions {
    /** Read an empty string as `ifEmpty`. */
    emptyOk?: boolean;
    ifEmpty?: undefined;
    /** Return `undefined` as is; otherwise it becomes now. */
    undefinedOk?: boolean;
    /** Return `null` as is; otherwise it becomes now. */
    nullOk?: boolean;
    /** Read numbers and numeric strings as Unix timestamps in seconds. */
    unixOk?: boolean;
    /** IANA zone: a wall-clock string is read in it, an instant is shown in it. */
    timezone?: string;
}

export type ParsedTs = Date | string | number | null | undefined;

const PARSE_TS_DEFAULTS: ParseTsOptions = {
    emptyOk: true,
    ifEmpty: undefined,
    undefinedOk: true,
    nullOk: true,
    unixOk: true,
    timezone: undefined,
};

/**
 * The lenient timestamp reader behind `dayToString`, `formattedDay` and `formattedTs`.
 * Numbers are Unix seconds, not milliseconds. Without a zone a non-numeric string is
 * returned untouched for the formatter to read.
 */
export function parseTs(ts: DateInput, opts?: ParseTsOptions): ParsedTs {
    const options = { ...PARSE_TS_DEFAULTS, ...opts };
    let value: ParsedTs = ts;

    if (options.emptyOk && value === '') value = options.ifEmpty;
    if (!options.undefinedOk && value === undefined) value = new Date();
    if (!options.nullOk && value === null) value = new Date();
    if (value === undefined || value === null) return value;

    if (
        options.unixOk &&
        (typeof value === 'number' || typeof value === 'string') &&
        !Number.isNaN(Number(value))
    ) {
        value = new Date(Number(value) * 1000);
    }

    if (options.timezone !== undefined && options.timezone !== null) {
        return wallTimeInZone(value, options.timezone);
    }

    return value;
}
